/**
 * 🚪 **THE PEN GATE, DRAWN** — D130, 2026-09-29.
 *
 * Two fixed posts at the sides of the cell, and between them a **leaf** of
 * three rails and a diagonal brace — the shape of a wooden field gate, and
 * Þórkötlustaðarétt's gates were wooden. **Shut**, the leaf spans the gateway;
 * **open**, it swings 90° on its hinge post, away from whoever placed it
 * (into the pen), and the gateway is clear.
 *
 * 🔴 **Every stick is a {@link Beam}, drawn by `torchmesh.ts`'s one beam
 * drawer, in the torch post's material.** Nothing new renders: *prefer a
 * material that is already in use* (`06_Traps.md`), and one beam drawer rather
 * than a second whose winding nobody has tested.
 *
 * 📏 **1.5 m tall, the height of the wall it stands in** — and the height a
 * shut gate stops a body at (`blocks.json`), so what you see is what stops you.
 */

import { CHUNK_SIZE, gateIsOpen, isGate, stateOf } from '@lofi/shared';
import type { Beam } from './torchmesh.ts';

/** 📏 The gate's height, metres — the stone wall's, so a gate sits flush in one. */
export const GATE_HEIGHT = 1.5;
/** A post's thickness, metres. */
const POST = 0.14;
/** A rail's thickness, metres. */
const RAIL = 0.09;
/** Where the posts stand, metres either side of the middle of the cell. */
const POST_AT = 0.5 - POST / 2;
/** Where the leaf ends, metres either side — just short of each post. */
const LEAF_AT = POST_AT - POST / 2 - RAIL / 2;
/** The three rails, metres up. */
const RAILS = [0.3, 0.78, 1.26] as const;

/** Which way each facing points — toward the player who placed the gate. */
const TOWARD: ReadonlyMap<string, readonly [number, number]> = new Map([
  ['north', [0, -1]],
  ['east', [1, 0]],
  ['south', [0, 1]],
  ['west', [-1, 0]],
]);

type V = readonly [number, number, number];

/**
 * 🚪 **The beams of one gate**, in the same coordinates as `x, y, z`.
 *
 * @param facing which way it faces — toward whoever placed it
 * @param open whether the leaf is swung open
 */
export function gateBeams(x: number, y: number, z: number, facing: string, open: boolean): Beam[] {
  const [fx, fz] = TOWARD.get(facing) ?? [0, -1];
  /* Across the gateway, and the way the leaf swings — away from the placer. */
  const [rx, rz] = [-fz, fx];
  const [sx, sz] = [-fx, -fz];
  const cx = x + 0.5;
  const cz = z + 0.5;
  /** A point `across` metres along the gateway and `out` metres toward the swing, `up` metres high. */
  const at = (across: number, up: number, out = 0): V => [
    cx + rx * across + sx * out,
    y + up,
    cz + rz * across + sz * out,
  ];

  const beams: Beam[] = [
    /* The two posts stay where they are, open or shut. */
    { base: at(-POST_AT, 0), top: at(-POST_AT, GATE_HEIGHT), thickness: POST },
    { base: at(POST_AT, 0), top: at(POST_AT, GATE_HEIGHT), thickness: POST },
  ];

  /** Where along the leaf a point is, 0 at the hinge and 1 at the latch. */
  const leaf = (t: number, up: number): V => {
    const length = LEAF_AT * 2;
    if (!open) return at(-LEAF_AT + t * length, up);
    /* Swung on the hinge post: the leaf now runs out from it, into the pen. */
    return at(-POST_AT, up, POST / 2 + RAIL / 2 + t * length);
  };
  for (const up of RAILS) beams.push({ base: leaf(0, up), top: leaf(1, up), thickness: RAIL });
  /* The brace, low at the hinge to high at the latch. */
  beams.push({ base: leaf(0, RAILS[0]), top: leaf(1, RAILS[2]), thickness: RAIL });
  return beams;
}

/** 🚪 Every gate in one chunk, as beams in chunk-local metres. */
export function gatesIn(blocks: Uint16Array): Beam[] {
  const out: Beam[] = [];
  for (let i = 0; i < blocks.length; i++) {
    /* eslint-disable-next-line security/detect-object-injection --
       A loop counter reading a Uint16Array. Same reasoning as torchesIn. */
    const id = blocks[i] ?? 0;
    if (!isGate(id)) continue;
    const facing = stateOf(id).facing;
    out.push(
      ...gateBeams(
        i % CHUNK_SIZE,
        Math.floor(i / (CHUNK_SIZE * CHUNK_SIZE)),
        Math.floor(i / CHUNK_SIZE) % CHUNK_SIZE,
        typeof facing === 'string' ? facing : 'north',
        gateIsOpen(id),
      ),
    );
  }
  return out;
}
